import React from 'react'

type TokenType = 'keyword' | 'string' | 'number' | 'comment' | 'tag' | 'attr' | 'punctuation' | 'plain'

interface Token {
  type: TokenType
  value: string
}

const TOKEN_REGEX = /\/\/[^\n]*|\/\*[\s\S]*?\*\/|"[^"]*"|'[^']*'|`[^`]*`|\b(import|from|export|default|function|const|let|var|return|class|extends|new|if|else|switch|case|break|template|script|setup)\b|\b\d+\.?\d*\b|<\/?[\w.-]+|[\w-]+(?==)|[{}()[\];:,=<>./]/g

const TOKEN_COLORS: Record<TokenType, string> = {
  keyword: 'text-purple-400',
  string: 'text-green-400',
  number: 'text-amber-400',
  comment: 'text-gray-500 italic',
  tag: 'text-sky-400',
  attr: 'text-orange-400',
  punctuation: 'text-gray-400',
  plain: 'text-gray-200',
}

function classifyToken(match: string): TokenType {
  if (match.startsWith('//') || match.startsWith('/*')) return 'comment'
  if (match.startsWith('"') || match.startsWith("'") || match.startsWith('`')) return 'string'
  if (/^(import|from|export|default|function|const|let|var|return|class|extends|new|if|else|switch|case|break|template|script|setup)$/.test(match)) return 'keyword'
  if (/^\d+\.?\d*$/.test(match)) return 'number'
  if (match.startsWith('<')) return 'tag'
  if (/^[\w-]+$/.test(match) && match.length > 1) return 'attr'
  if (/^[{}()[\];:,=<>./]$/.test(match)) return 'punctuation'
  return 'plain'
}

function tokenize(code: string): Token[] {
  const tokens: Token[] = []
  let lastIndex = 0

  for (const match of code.matchAll(new RegExp(TOKEN_REGEX, 'g'))) {
    const start = match.index!
    if (start > lastIndex) {
      tokens.push({ type: 'plain', value: code.slice(lastIndex, start) })
    }
    tokens.push({ type: classifyToken(match[0]), value: match[0] })
    lastIndex = start + match[0].length
  }

  if (lastIndex < code.length) {
    tokens.push({ type: 'plain', value: code.slice(lastIndex) })
  }

  return tokens
}

export function SyntaxHighlightedCode({ code }: { code: string }) {
  const tokens = tokenize(code)

  return (
    <pre className="text-sm leading-relaxed font-mono overflow-auto">
      <code>
        {tokens.map((token, i) => (
          <span key={i} className={TOKEN_COLORS[token.type]}>
            {token.value}
          </span>
        ))}
      </code>
    </pre>
  )
}
